/**
 * Per-vertex geodesic distance cache (auto-skin v2, plan D-v2-1).
 *
 * Voxelization and the geodesic walk happen once per mesh; the result is sampled down to one
 * distance per unique vertex position per bone. The weight solver then re-solves from this cache
 * alone, which is what makes live retuning cheap.
 *
 * Seam duplicates (a `toNonIndexed()` mesh duplicates every vertex) are welded by quantized
 * position, so both sides of a UV seam always get identical weights.
 *
 * Pure module: no DOM, no editor state. The voxel grid and the per-cell fields are dropped as
 * soon as sampling is done — they are not part of the cache.
 */

import type { BufferGeometry } from 'three';

import { AppError } from '../../shared/errors';
import type { CanonicalBoneId, Vec3Tuple } from '../../shared/types';
import type { BoneSegment } from '../skinning';
import type { VertexAdjacency, VertexDistanceCache } from './types';
import { computeBoneDistanceFields } from './geodesic';
import { buildVoxelGrid, cellOfPoint, nearestDomainCell } from './voxelGrid';

const DEFAULT_WELD_EPSILON = 1e-6;
/** How far a sampled vertex may look for a domain cell when its own cell came out exterior. */
const SAMPLE_SEARCH_CELLS = 3;

export interface DistanceCacheOptions {
  /** Voxels along the longest bbox axis. */
  resolution: number;
  meshName: string;
  /** Positions closer than this in every axis are one unique vertex. Default 1e-6. */
  weldEpsilon?: number;
}

interface WeldResult {
  uniqueCount: number;
  uniqueOf: Int32Array;
  verticesOfUnique: VertexAdjacency;
  /** One representative position per unique vertex, x,y,z interleaved. */
  representative: Float32Array;
}

function readPositions(geometry: BufferGeometry): { positions: Float32Array; vertexCount: number } {
  const attribute = geometry.getAttribute('position');
  if (!attribute) {
    throw new AppError('SKIN_NO_POSITION', 'The mesh geometry has no position attribute.');
  }
  const vertexCount = attribute.count;
  const positions = new Float32Array(vertexCount * 3);
  for (let v = 0; v < vertexCount; v++) {
    positions[v * 3] = attribute.getX(v);
    positions[v * 3 + 1] = attribute.getY(v);
    positions[v * 3 + 2] = attribute.getZ(v);
  }
  return { positions, vertexCount };
}

function weldVertices(positions: Float32Array, vertexCount: number, epsilon: number): WeldResult {
  const eps = epsilon > 0 ? epsilon : DEFAULT_WELD_EPSILON;
  const uniqueOf = new Int32Array(vertexCount);
  const keys = new Map<string, number>();
  const repList: number[] = [];
  let uniqueCount = 0;

  for (let v = 0; v < vertexCount; v++) {
    const x = positions[v * 3];
    const y = positions[v * 3 + 1];
    const z = positions[v * 3 + 2];
    const key = `${Math.round(x / eps)},${Math.round(y / eps)},${Math.round(z / eps)}`;
    let id = keys.get(key);
    if (id === undefined) {
      id = uniqueCount++;
      keys.set(key, id);
      repList.push(x, y, z);
    }
    uniqueOf[v] = id;
  }

  const offsets = new Int32Array(uniqueCount + 1);
  for (let v = 0; v < vertexCount; v++) offsets[uniqueOf[v] + 1]++;
  for (let u = 0; u < uniqueCount; u++) offsets[u + 1] += offsets[u];
  const cursor = Int32Array.from(offsets.subarray(0, uniqueCount));
  const neighbors = new Int32Array(vertexCount);
  for (let v = 0; v < vertexCount; v++) neighbors[cursor[uniqueOf[v]]++] = v;

  return {
    uniqueCount,
    uniqueOf,
    verticesOfUnique: { offsets, neighbors },
    representative: Float32Array.from(repList),
  };
}

function buildAdjacency(
  index: ArrayLike<number> | null,
  vertexCount: number,
  uniqueOf: Int32Array,
  uniqueCount: number,
): VertexAdjacency {
  const triCount = index ? Math.floor(index.length / 3) : Math.floor(vertexCount / 3);
  const edges = new Set<number>();

  for (let t = 0; t < triCount; t++) {
    const a = uniqueOf[index ? index[t * 3] : t * 3];
    const b = uniqueOf[index ? index[t * 3 + 1] : t * 3 + 1];
    const c = uniqueOf[index ? index[t * 3 + 2] : t * 3 + 2];
    if (a !== b) edges.add(a < b ? a * uniqueCount + b : b * uniqueCount + a);
    if (b !== c) edges.add(b < c ? b * uniqueCount + c : c * uniqueCount + b);
    if (c !== a) edges.add(c < a ? c * uniqueCount + a : a * uniqueCount + c);
  }

  const offsets = new Int32Array(uniqueCount + 1);
  for (const key of edges) {
    const a = Math.floor(key / uniqueCount);
    const b = key - a * uniqueCount;
    offsets[a + 1]++;
    offsets[b + 1]++;
  }
  for (let u = 0; u < uniqueCount; u++) offsets[u + 1] += offsets[u];

  const cursor = Int32Array.from(offsets.subarray(0, uniqueCount));
  const neighbors = new Int32Array(offsets[uniqueCount]);
  for (const key of edges) {
    const a = Math.floor(key / uniqueCount);
    const b = key - a * uniqueCount;
    neighbors[cursor[a]++] = b;
    neighbors[cursor[b]++] = a;
  }

  return { offsets, neighbors };
}

/**
 * Voxelize, walk the geodesics from every bone segment, and sample one distance per unique
 * vertex per bone. `distances` is bone-major: `distances[b * uniqueCount + u]`.
 */
export function buildVertexDistanceCache(
  geometry: BufferGeometry,
  segments: BoneSegment[],
  opts: DistanceCacheOptions,
): VertexDistanceCache {
  if (segments.length === 0) {
    throw new AppError('SKIN_NO_SEGMENTS', 'Cannot build a distance cache without bone segments.');
  }

  const { positions, vertexCount } = readPositions(geometry);
  const indexAttribute = geometry.index;
  const index: ArrayLike<number> | null = indexAttribute ? indexAttribute.array : null;

  const weld = weldVertices(positions, vertexCount, opts.weldEpsilon ?? DEFAULT_WELD_EPSILON);
  const adjacency = buildAdjacency(index, vertexCount, weld.uniqueOf, weld.uniqueCount);

  let minX = Infinity;
  let minY = Infinity;
  let minZ = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let maxZ = -Infinity;
  for (let v = 0; v < vertexCount; v++) {
    const x = positions[v * 3];
    const y = positions[v * 3 + 1];
    const z = positions[v * 3 + 2];
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (z < minZ) minZ = z;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
    if (z > maxZ) maxZ = z;
  }
  const extent = Math.max(maxX - minX, maxY - minY, maxZ - minZ);

  const grid = buildVoxelGrid(positions, index, { resolution: opts.resolution });

  const geodesicStart = performance.now();
  const fields = computeBoneDistanceFields(grid, segments);
  const geodesicMs = performance.now() - geodesicStart;

  const boneCount = segments.length;
  const uniqueCount = weld.uniqueCount;
  const distances = new Float32Array(boneCount * uniqueCount);
  distances.fill(Infinity);

  const point: Vec3Tuple = [0, 0, 0];
  for (let u = 0; u < uniqueCount; u++) {
    point[0] = weld.representative[u * 3];
    point[1] = weld.representative[u * 3 + 1];
    point[2] = weld.representative[u * 3 + 2];

    let compact = grid.domainIndex[cellOfPoint(grid, point)];
    if (compact < 0) compact = nearestDomainCell(grid, point, SAMPLE_SEARCH_CELLS);
    if (compact < 0) continue;

    for (let b = 0; b < boneCount; b++) {
      distances[b * uniqueCount + u] = fields[b].distances[compact];
    }
  }

  const pulledInside: CanonicalBoneId[] = [];
  for (const field of fields) if (field.pulledInside) pulledInside.push(field.boneId);

  return {
    meshName: opts.meshName,
    vertexCount,
    uniqueCount,
    uniqueOf: weld.uniqueOf,
    verticesOfUnique: weld.verticesOfUnique,
    adjacency,
    boneIds: segments.map((segment) => segment.id),
    distances,
    extent,
    gridStats: {
      ...grid.stats,
      resolution: opts.resolution,
      geodesicMs,
      pulledInside,
    },
  };
}

/** Index into `cache.boneIds` of the closest bone to a unique vertex; -1 if none is reachable. */
export function nearestBoneOf(cache: VertexDistanceCache, unique: number): number {
  const count = cache.boneIds.length;
  let best = -1;
  let bestDistance = Infinity;
  for (let b = 0; b < count; b++) {
    const d = cache.distances[b * cache.uniqueCount + unique];
    if (d < bestDistance) {
      bestDistance = d;
      best = b;
    }
  }
  return best;
}
